import { z } from 'zod';
/**
 * LLM API 릴레이 alias 형식. 외부 OpenAI-호환 클라이언트가 `model` 필드에 넣는 값.
 * 영소문자/숫자/`._:-` 만 허용 (Ollama 의 tag 표기 `gemma4:31b` 호환).
 */
export const RelayAliasSchema = z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9._:-]+$/, '소문자/숫자/. : _ - 만 사용할 수 있습니다.');
export const ProviderSchema = z.enum([
    'openai',
    'anthropic',
    'gemini',
    'gcp',
    // GCP Vertex AI 의 Anthropic Claude 모델. 인증/설정(SA JSON + projectId/location)은 'gcp' 와
    // 동일하지만 Anthropic Messages API 로 호출하므로 별도 어댑터로 라우팅한다.
    'gcp-anthropic',
    'vllm',
    'mlx',
    'ollama',
    'lm-studio',
    'openai-compatible',
]);
/**
 * GCP Vertex AI 계열 provider (Gemini='gcp', Claude='gcp-anthropic').
 * service account JSON + projectId/location 인증·설정을 공유한다.
 */
export function isVertexProvider(p) {
    return p === 'gcp' || p === 'gcp-anthropic';
}
/**
 * Provider 별 추가 설정. 현재는 GCP Vertex AI 용 projectId/location 만 사용.
 * 다른 provider 가 향후 자체 키를 추가하면 본 객체 안에 펼침.
 *
 * 시크릿 (service account JSON 등) 은 본 객체에 담지 말 것 — `apiKey` 필드를
 * 통해 암호화된 컬럼으로 저장한다.
 */
export const ProviderConfigSchema = z.object({
    /** GCP project id. Vertex AI 호출 시 필수. */
    projectId: z.string().trim().max(120).optional(),
    /** GCP region (us-central1, asia-northeast3 등). Vertex AI 호출 시 필수. */
    location: z.string().trim().max(60).optional(),
});
/** Anthropic 계열(직접 + Vertex Claude)만 extended thinking 지원. */
export function providerSupportsExtendedThinking(p) {
    return p === 'anthropic' || p === 'gcp-anthropic';
}
/**
 * chat template 이 "단일 선두 system" 만 허용하는 모델군.
 * Qwen3 계열은 index 0 이 아닌 system 메시지를 만나면
 * raise_exception("System message must be at the beginning.") 을 던져 vLLM 이 400 을 반환한다.
 * (chat handler 는 매 turn persona·컨텍스트·가이드라인 system 메시지를 여러 개 연속 push 한다.)
 * Gemma 등은 다중 system 을 관용·병합하므로 정규화 대상이 아니다 — 해당 경로는 건드리지 않는다.
 */
export function modelRequiresLeadingSystem(model) {
    return /qwen/i.test(model);
}
/**
 * 클라우드 상용 API provider — 대용량 컨텍스트 창 보유.
 * (grok/xAI 는 현재 enum 미존재 → openai-compatible 로 등록되며 추후 'xai' 추가 시 포함.)
 */
const CLOUD_PROVIDERS = new Set([
    'openai',
    'anthropic',
    'gemini',
    'gcp',
    'gcp-anthropic',
]);
/**
 * endpoint 에 context_window 가 명시되지 않았을 때의 provider 별 기본값.
 * - 클라우드 상용 API: 200000 (Claude 200k / GPT·Gemini 대용량)
 * - 로컬·자체호스팅 (vllm/mlx/ollama/lm-studio/openai-compatible): 32000
 *   (사용자가 endpoint 의 context_window 로 직접 덮어쓸 수 있음)
 */
export function defaultContextWindow(p) {
    return CLOUD_PROVIDERS.has(p) ? 200000 : 32000;
}
/**
 * 도구 사용 불가 모델 패턴 — Llama-4 계열.
 *
 * 차단 사유: NVIDIA NVFP4 빌드 / Meta Llama-4-Scout / Maverick 모두 vLLM tool calling 에서
 * 함수명 placeholder (`func_name`) 호출, 가짜 테이블명 SQL, 본문에 위조 데이터 hallucinate 가
 * fundamental 한 한계. vLLM Issue #22917 (Closed not-planned) + HF Llama-4-Scout PR #78 적용
 * 후에도 본질 해결 안 됨. 운영 안정성 우선해 endpoint 등록 자체를 차단.
 *
 * 매칭 예: `llama-4-scout-nvfp4`, `Llama-4-Maverick-17B`, `meta-llama/Llama-4-Scout`,
 *   `nvidia-Llama-4-Scout-17B-16E-Instruct-NVFP4`, `llama4-scout`.
 * 비매칭: `llama-3.1-70b`, `llama-40b`, `llama4b`, `gemma-4-31b`, `gpt-4`.
 */
export const BLOCKED_MODEL_PATTERN = /\bllama[-_]?4\b/i;
export const BLOCKED_MODEL_MESSAGE = 'Llama-4 계열 모델 (Scout / Maverick) 은 도구 호출 (skill · 대시보드 · SQL 분석) 에서 환각·placeholder 호출이 잦아 endpoint 로 등록할 수 없습니다. Gemma-4 또는 다른 모델을 사용해주세요.';
/** 등록·수정 폼이 받은 model 이름이 차단 패턴에 걸리는지 검사. true 면 등록 불가. */
export function isBlockedModel(model) {
    return BLOCKED_MODEL_PATTERN.test(model);
}
/**
 * baseUrl 필드는 URL 또는 빈 문자열을 허용한다.
 * OpenAI / Anthropic / Gemini 같은 클라우드 SDK 는 baseUrl 을 비우면 SDK 자체의
 * 공식 기본값 (api.openai.com / api.anthropic.com / generativelanguage.googleapis.com)
 * 을 사용하므로, 사용자가 굳이 외울 필요가 없다. 로컬 모델 (vllm/mlx/ollama 등) 은
 * 사용자가 직접 URL 을 적어줘야 하지만, 그건 폼 제출 단계에서 검증하지 않고
 * UX 단에서 (provider preset) 자동 채워준다.
 */
const EndpointBaseUrlSchema = z.union([z.literal(''), z.string().url()]);
/** 관리자용 — API 키 제외한 필드 전부. */
export const LlmEndpointSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    displayName: z.string().nullable(),
    description: z.string().nullable(),
    provider: ProviderSchema,
    baseUrl: EndpointBaseUrlSchema,
    model: z.string(),
    extraHeaders: z.record(z.string()),
    /** Provider 별 추가 설정 (GCP 의 projectId/location 등). 시크릿은 여기에 넣지 말 것. */
    providerConfig: ProviderConfigSchema,
    isDefault: z.boolean(),
    isEnabledForUsers: z.boolean(),
    extendedThinking: z.boolean(),
    /** OpenAI vision spec 의 image_url content part 처리 가능. wiki ingest caption 후보. */
    supportsVision: z.boolean(),
    /** 시스템 전체의 기본 vision target. endpoint+bonding 통틀어 single true. */
    isVisionDefault: z.boolean(),
    /** OpenAI input_audio content part 처리 가능. ASR / 음성→텍스트 후보. */
    supportsAudio: z.boolean(),
    /** 시스템 전체의 기본 audio target. endpoint+bonding 통틀어 single true. */
    isAudioDefault: z.boolean(),
    /** 이미지 생성 (Gemini Nano Banana 등) 전용 엔드포인트 여부. image.generate 툴 후보. */
    supportsImageGeneration: z.boolean(),
    /** 시스템 전체의 기본 이미지 생성 target (endpoint 전용 — bonding 미지원). */
    isImageGenerationDefault: z.boolean(),
    /** /v1/embeddings 엔드포인트 노출 여부. wiki 페이지 임베딩 후보. */
    supportsEmbedding: z.boolean(),
    /** embedding 차원 (BGE-M3=1024, OpenAI text-embedding-3-small=1536). supportsEmbedding=true 일 때만 의미. */
    embeddingDim: z.number().int().nullable(),
    /** 모델의 컨텍스트 윈도우 (토큰). 챗 turn 시작 시 추정 토큰 / 이 값 비율로 임계치 경고. NULL = 32000 fallback. */
    contextWindow: z.number().int().nullable(),
    /** 동시 요청 상한 (전역 세마포어). 사용자 챗은 (값-1) 까지, 1 슬롯은 시스템 운영 예약. NULL = 무제한. */
    maxConcurrent: z.number().int().nullable(),
    /** 한 응답의 최대 출력 토큰. NULL = 어댑터/모델 기본(OpenAI호환·Anthropic 8192). */
    maxOutputTokens: z.number().int().nullable(),
    sortOrder: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
    hasApiKey: z.boolean(),
    lastProbedAt: z.string().nullable(),
    lastProbeOk: z.boolean().nullable(),
    lastProbeError: z.string().nullable(),
    lastProbeModelCount: z.number().int().nullable(),
    /** API 릴레이 외부 노출 alias. NULL 이면 /v1/models 에 노출되지 않음. */
    relayAlias: z.string().nullable(),
});
export const LlmEndpointListSchema = z.object({
    endpoints: z.array(LlmEndpointSchema),
});
export const CreateLlmEndpointRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    displayName: z.string().trim().max(80).nullable().optional(),
    description: z.string().trim().max(400).nullable().optional(),
    provider: ProviderSchema,
    baseUrl: EndpointBaseUrlSchema,
    apiKey: z.string().min(1),
    model: z.string().trim().min(1).max(200),
    extraHeaders: z.record(z.string()).optional(),
    /** Provider 별 추가 설정. GCP 일 때 projectId/location 필수. */
    providerConfig: ProviderConfigSchema.optional(),
    isDefault: z.boolean().optional(),
    isEnabledForUsers: z.boolean().optional(),
    extendedThinking: z.boolean().optional(),
    supportsVision: z.boolean().optional(),
    isVisionDefault: z.boolean().optional(),
    supportsAudio: z.boolean().optional(),
    isAudioDefault: z.boolean().optional(),
    supportsImageGeneration: z.boolean().optional(),
    isImageGenerationDefault: z.boolean().optional(),
    supportsEmbedding: z.boolean().optional(),
    embeddingDim: z.number().int().min(1).max(8192).nullable().optional(),
    /** 모델의 컨텍스트 윈도우 (토큰). NULL/omit 이면 32000 fallback. 1k ~ 2M 까지 허용. */
    contextWindow: z.number().int().min(1000).max(2000000).nullable().optional(),
    /** 동시 요청 상한 (전역 세마포어). omit 이면 기본 10, null 이면 무제한. 1~256. */
    maxConcurrent: z.number().int().min(1).max(256).nullable().optional(),
    /** 한 응답의 최대 출력 토큰. omit/null 이면 어댑터·모델 기본(8192). 256~200000. */
    maxOutputTokens: z.number().int().min(256).max(200000).nullable().optional(),
    sortOrder: z.number().int().optional(),
    /** 빈 문자열 또는 null 은 alias 미설정. 본딩 alias 와 통틀어 unique. */
    relayAlias: RelayAliasSchema.nullable().optional(),
})
    .superRefine((data, ctx) => {
    if (isBlockedModel(data.model)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['model'],
            message: BLOCKED_MODEL_MESSAGE,
        });
    }
    if (data.provider !== undefined && isVertexProvider(data.provider)) {
        validateGcpFields(data, ctx);
    }
    validateImageGenerationFields(data, ctx);
});
/**
 * 이미지 생성 default 가드. supportsImageGeneration=false 인데 기본 이미지 생성 target 으로
 * 지정하려 하면 거부. (UI 도 막지만 서버 단일 진실원천으로 한 번 더.)
 */
function validateImageGenerationFields(data, ctx) {
    if (data.isImageGenerationDefault === true && data.supportsImageGeneration !== true) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['isImageGenerationDefault'],
            message: '기본 이미지 생성 모델은 "이미지 생성 지원" 이 켜진 엔드포인트만 지정할 수 있습니다.',
        });
    }
}
/**
 * GCP Vertex AI 필드 검증. service account JSON 이 valid JSON 인지 + projectId/location 필수.
 * apiKey 가 비어있을 수 있는 update 흐름에서도 동일 함수가 안전하게 동작하도록
 * apiKey 미입력은 통과시킨다 (update 시 기존 키 유지 의미).
 */
function validateGcpFields(data, ctx) {
    if (!data.providerConfig?.projectId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerConfig', 'projectId'],
            message: 'GCP 는 project id 가 필요합니다.',
        });
    }
    if (!data.providerConfig?.location) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerConfig', 'location'],
            message: 'GCP 는 location (region) 이 필요합니다.',
        });
    }
    if (data.apiKey && data.apiKey.length > 0) {
        try {
            const parsed = JSON.parse(data.apiKey);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                throw new Error('object expected');
            }
            if (typeof parsed.private_key !== 'string') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['apiKey'],
                    message: 'service account JSON 에 private_key 필드가 없습니다.',
                });
            }
        }
        catch {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['apiKey'],
                message: 'service account JSON 형식이 올바르지 않습니다.',
            });
        }
    }
}
export const UpdateLlmEndpointRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    displayName: z.string().trim().max(80).nullable().optional(),
    description: z.string().trim().max(400).nullable().optional(),
    provider: ProviderSchema.optional(),
    baseUrl: EndpointBaseUrlSchema.optional(),
    /** 비우거나 omit 하면 기존 API 키 유지. */
    apiKey: z.string().min(1).optional(),
    model: z.string().trim().min(1).max(200).optional(),
    extraHeaders: z.record(z.string()).optional(),
    /** Provider 별 추가 설정. omit 이면 기존 유지, 명시 객체면 전체 교체. */
    providerConfig: ProviderConfigSchema.optional(),
    isDefault: z.boolean().optional(),
    isEnabledForUsers: z.boolean().optional(),
    extendedThinking: z.boolean().optional(),
    supportsVision: z.boolean().optional(),
    isVisionDefault: z.boolean().optional(),
    supportsAudio: z.boolean().optional(),
    isAudioDefault: z.boolean().optional(),
    supportsImageGeneration: z.boolean().optional(),
    isImageGenerationDefault: z.boolean().optional(),
    supportsEmbedding: z.boolean().optional(),
    embeddingDim: z.number().int().min(1).max(8192).nullable().optional(),
    contextWindow: z.number().int().min(1000).max(2000000).nullable().optional(),
    /** 동시 요청 상한. null 명시면 무제한, omit 이면 기존값 유지. 1~256. */
    maxConcurrent: z.number().int().min(1).max(256).nullable().optional(),
    /** 한 응답의 최대 출력 토큰. null 명시면 기본(8192)으로, omit 이면 기존값 유지. 256~200000. */
    maxOutputTokens: z.number().int().min(256).max(200000).nullable().optional(),
    sortOrder: z.number().int().optional(),
    /** null 명시면 alias 제거, omit 이면 유지. */
    relayAlias: RelayAliasSchema.nullable().optional(),
})
    .superRefine((data, ctx) => {
    if (data.model !== undefined && isBlockedModel(data.model)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['model'],
            message: BLOCKED_MODEL_MESSAGE,
        });
    }
    if (data.provider !== undefined && isVertexProvider(data.provider)) {
        validateGcpFields(data, ctx);
    }
});
export const ProbeDraftRequestSchema = z
    .object({
    provider: ProviderSchema,
    baseUrl: EndpointBaseUrlSchema,
    /** 비우거나 omit 하면 endpointId 의 저장된 키를 사용. */
    apiKey: z.string().min(1).optional(),
    /** 편집 모드에서 키 재입력 없이 저장된 키로 테스트할 때 사용. */
    endpointId: z.string().uuid().optional(),
    extraHeaders: z.record(z.string()).optional(),
    /** GCP 의 projectId/location 등 — provider 별 추가 설정. omit 이면 endpointId 의 저장값 사용. */
    providerConfig: ProviderConfigSchema.optional(),
})
    .refine((v) => Boolean(v.apiKey) || Boolean(v.endpointId), {
    message: 'apiKey 또는 endpointId 중 하나는 필요합니다.',
    path: ['apiKey'],
});
export const ProbeResponseSchema = z.object({
    ok: z.boolean(),
    models: z.array(z.string()),
    error: z.string().optional(),
    /** `models` 중 등록 차단 패턴에 걸리는 모델 — 연결은 성공해도 등록 시 선택 금지. */
    blockedModels: z.array(z.string()).optional(),
});
/** 사용자 뷰 — 드롭다운용 축소 정보. API 정보/모델 ID 노출 X. */
export const ChatModelSchema = z.object({
    id: z.string().uuid(),
    /** 가상 LLM(본딩) 인지 단일 엔드포인트인지 구분. */
    kind: z.enum(['endpoint', 'bonding']),
    displayName: z.string(),
    description: z.string().nullable(),
    /** 본딩은 멤버 provider 가 섞일 수 있어 null. */
    provider: ProviderSchema.nullable(),
    isDefault: z.boolean(),
    supportsExtendedThinking: z.boolean(),
    defaultExtendedThinking: z.boolean(),
});
export const ChatModelListSchema = z.object({
    models: z.array(ChatModelSchema),
});
/* ── 본딩 (LLM Bonding) ─────────────────────────────────────────── */
/**
 * 본딩 멤버 라우팅 전략.
 * - `random`: health-aware 랜덤 균등 분배 (기본, 기존 동작).
 * - `priority`: 관리자가 정한 멤버 순서(position)대로 위에서부터 사용. 상위 엔드포인트가
 *   사용 불가(가용성 오류 / 슬롯 full)면 다음 순서로 내려간다. (내부 GPU → 외부 API spillover)
 */
export const BondingRoutingStrategySchema = z.enum(['random', 'priority']);
export const LlmBondingSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    displayName: z.string().nullable(),
    description: z.string().nullable(),
    isEnabled: z.boolean(),
    isEnabledForUsers: z.boolean(),
    isDefault: z.boolean(),
    /** 시스템 전체의 기본 vision target (endpoint+bonding 통틀어 single). 이 본딩의 모든 멤버는 supportsVision=true 여야 함. */
    isVisionDefault: z.boolean(),
    /** 시스템 전체의 기본 audio target (endpoint+bonding 통틀어 single). 이 본딩의 모든 멤버는 supportsAudio=true 여야 함. */
    isAudioDefault: z.boolean(),
    sortOrder: z.number().int(),
    /** 멤버 라우팅 전략 (random=랜덤 균등 / priority=우선순위 순서). */
    routingStrategy: BondingRoutingStrategySchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    /** 멤버 endpoint id 배열. priority 모드에서는 이 순서가 곧 우선순위 (position 순). */
    memberIds: z.array(z.string().uuid()),
    /** API 릴레이 외부 노출 alias. NULL 이면 /v1/models 에 노출되지 않음. */
    relayAlias: z.string().nullable(),
});
export const LlmBondingListSchema = z.object({
    bondings: z.array(LlmBondingSchema),
});
/** 본딩 단건 상세 — memberIds 와 동일 순서로 멤버 엔드포인트 객체 포함. */
export const LlmBondingDetailSchema = LlmBondingSchema.extend({
    members: z.array(LlmEndpointSchema),
});
/** 일괄 probe 응답 — list/probe-all 공용. */
export const ProbeAllResponseSchema = z.object({
    updated: z.number().int(),
});
/** 본딩에서 멤버 단건 제거. */
export const RemoveBondingMemberRequestSchema = z.object({
    memberId: z.string().uuid(),
});
export const CreateLlmBondingRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    displayName: z.string().trim().max(80).nullable().optional(),
    description: z.string().trim().max(400).nullable().optional(),
    memberIds: z.array(z.string().uuid()).min(2).max(16),
    isEnabled: z.boolean().optional(),
    isEnabledForUsers: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    isVisionDefault: z.boolean().optional(),
    isAudioDefault: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    /** 미지정 시 서버가 'random' 으로 기본 설정. */
    routingStrategy: BondingRoutingStrategySchema.optional(),
    relayAlias: RelayAliasSchema.nullable().optional(),
});
export const UpdateLlmBondingRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(80).optional(),
    displayName: z.string().trim().max(80).nullable().optional(),
    description: z.string().trim().max(400).nullable().optional(),
    memberIds: z.array(z.string().uuid()).min(2).max(16).optional(),
    isEnabled: z.boolean().optional(),
    isEnabledForUsers: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    isVisionDefault: z.boolean().optional(),
    isAudioDefault: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    routingStrategy: BondingRoutingStrategySchema.optional(),
    relayAlias: RelayAliasSchema.nullable().optional(),
})
    .refine((v) => !v.memberIds || v.memberIds.length >= 2, {
    message: '멤버는 최소 2개여야 합니다.',
    path: ['memberIds'],
});
/** 본딩 probe 결과 — 각 멤버별 연결 테스트 결과 배열. */
export const LlmBondingProbeMemberResultSchema = z.object({
    memberId: z.string().uuid(),
    name: z.string(),
    ok: z.boolean(),
    models: z.array(z.string()),
    error: z.string().optional(),
});
export const LlmBondingProbeResponseSchema = z.object({
    members: z.array(LlmBondingProbeMemberResultSchema),
});
/** 저장 전 폼에서 호출. memberIds 로만 probe. */
export const LlmBondingProbeDraftRequestSchema = z.object({
    memberIds: z.array(z.string().uuid()).min(1).max(16),
});
/**
 * LLM 엔드포인트별 동시성 슬롯 현황 (관리자 모니터링). endpointId 로 LlmEndpoint 와 병합해 표시.
 * - activeUser/activeSystem: 유효(미만료)한 kind 별 점유 수.
 * - stale: lease 만료됐으나 아직 회수 안 된 잔여 (reaper 가 곧 정리).
 * - oldestAcquiredAt: 가장 오래된 active 슬롯 획득 시각 (좀비/누수 진단).
 */
export const LlmConcurrencyRowSchema = z.object({
    endpointId: z.string(),
    activeUser: z.number().int(),
    activeSystem: z.number().int(),
    stale: z.number().int(),
    oldestAcquiredAt: z.string().nullable(),
});
export const LlmConcurrencyStatusSchema = z.object({
    rows: z.array(LlmConcurrencyRowSchema),
});
/**
 * 슬롯 단위 상세 (관리자 진단용). 집계(Row)로는 안 풀리는 슬롯의 원인을 못 보므로 개별 행을 노출.
 * - holder: `pid@host:runId` (어느 워커가 점유 중인지).
 * - stale: lease 만료 여부 (true 면 회수 대상이나 아직 남아있음).
 */
export const LlmConcurrencySlotSchema = z.object({
    token: z.string(),
    endpointId: z.string(),
    /** 워커 식별 (`pid@host`). 사용자 정체는 actor* 필드로 별도 노출. */
    holder: z.string(),
    kind: z.enum(['user', 'system']),
    acquiredAt: z.string(),
    expiresAt: z.string(),
    stale: z.boolean(),
    /** 사용자 turn 의 점유자 uuid (system 슬롯/구버전 슬롯은 null). */
    actorUserId: z.string().nullable(),
    /** users.display_name. actorUserId 가 null 이거나 사용자가 삭제됐으면 null. */
    actorName: z.string().nullable(),
    /** users.email. 위와 동일. */
    actorEmail: z.string().nullable(),
    /**
     * 시스템 슬롯의 작업 라벨 (예: `wiki-ingest:meta`, `chat-compaction`). user 슬롯이거나
     * 라벨이 부여되지 않은 구버전 system 슬롯이면 null.
     */
    systemTask: z.string().nullable(),
});
export const LlmConcurrencySlotsResponseSchema = z.object({
    slots: z.array(LlmConcurrencySlotSchema),
});
/** 특정 슬롯 강제 해제 — 만료 여부 무관 token 으로 DELETE. */
export const LlmConcurrencyReleaseRequestSchema = z.object({ token: z.string().uuid() });
//# sourceMappingURL=llm.js.map